import React, { ReactElement, useContext } from 'react';
import { createUseStyles } from 'react-jss';
import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import { IconUser } from '@wds/icons';
import { StaticContext } from '../StaticContext';

const useStyles = createUseStyles(theme => ({
  banner: {
    backgroundColor: theme.palette.error.main,
    borderBottom: `1px solid ${theme.palette.border.main}`,
    position: 'sticky',
    top: 0,
    zIndex: 1000,
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 16px',
    color: theme.palette.text.light,
  },
  icon: {
    marginRight: '8px',
  },
  text: {
    fontWeight: 'bold',
  },
}));

export type AdminImpersonationBannerProps = {
  className?: string;
};

/**
 * Banner component that displays when an admin is impersonating another user
 * Shows at the top of the page to clearly indicate impersonation mode
 */
const AdminImpersonationBanner = ({
  className,
}: AdminImpersonationBannerProps = {}): ReactElement => {
  const { banner, content, icon, text } = useStyles();
  const { user } = useContext(StaticContext);

  return (
    <Box
      className={`${banner} ${className || ''}`}
      role="alert"
      aria-label="Admin impersonation active"
    >
      <div className={content}>
        <IconUser title="Admin Impersonation" className={icon} size="sm" />
        <Typography
          className={text}
          variant="body"
          component="span"
          color="light"
        >
          <>
            Admin Impersonation Active
            {user.username || user.id ? (
              <>
                &nbsp;- Viewing as
                {user.username && <>&nbsp;{`user: ${user.username}`}</>}
                {user.id && <>&nbsp;{`(ID: ${user.id})`}</>}
              </>
            ) : (
              <>&nbsp;- You are viewing as another user</>
            )}
          </>
        </Typography>
      </div>
    </Box>
  );
};

export default AdminImpersonationBanner;
